import { clsx } from "clsx";
import { Component } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, SubmissionError, type Validator, formValueSelector, reduxForm } from "redux-form";

import { Card, TabItem } from "widgets";

import style from "../core/overlays/styles/Dialogs.module.scss";

import { handleTabInForm, simpleCharactersValidation, simpleFieldValidation } from "../../app-utils/form.js";
import { type InputRoleInput, type Role } from "../../graphql.js";
import { createRole, updateRole } from "../../redux/modules/roles-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { TabDialog } from "../core/overlays/Dialogs.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import { RoleCapabilitiesAdminCard, RolePlaybooksAdminCard, RoleUsersAdminCard } from "./RoleAdminCards.js";
import { type ISelectionModel } from "@/app-utils/selection-model.js";
import { MAX_LENGTH } from "@/settings/settings.ts";
import { SelForm } from "@/ui-modules/core/form/layout/Form.js";

interface IEditRoleDialogOwnProps {
  role: Role;
  buttonLabel: string;
  titleMessage: string;
  openTabKey: string; // tab open by default, corresponding to the last viewed tab
  onSelect: (role: Role) => void;
  onCancel: () => void;
}

interface IEditRoleDialogAppStateProps {
  allRoles: Role[];
  initialValues: Partial<FormData>;
  roleName: string;
}

interface IEditRoleDialogDispatchProps {
  updateRole: (payload: InputRoleInput) => Promise<Role>;
  createRole: (payload: InputRoleInput) => Promise<Role>;
}

export interface IEditRoleDialogProps
  extends IEditRoleDialogAppStateProps,
    IEditRoleDialogDispatchProps,
    IEditRoleDialogOwnProps,
    InjectedFormProps<FormData, any> {}

function mapStateToProps(state: IAppState, ownProps: IEditRoleDialogOwnProps): IEditRoleDialogAppStateProps {
  const roleName: string = formValueSelector(EditRoleDialogImpl.ROLE_DIALOG_FORM)(
    state,
    EditRoleDialogImpl.ROLE_DIALOG_FIELD_NAME,
  );

  return {
    allRoles: state.rolesAdmin.roles,
    initialValues: {
      [EditRoleDialogImpl.ROLE_DIALOG_FIELD_NAME]: ownProps.role.name || "",
      [EditRoleDialogImpl.ROLE_DIALOG_FIELD_DESCRIPTION]: ownProps.role.description,
    },
    roleName: roleName,
  };
}

function mapDispatchToProps(dispatch): IEditRoleDialogDispatchProps {
  return {
    updateRole: (payload: InputRoleInput) => dispatch(updateRole(payload)),
    createRole: (payload: InputRoleInput) => dispatch(createRole(payload)),
  };
}

interface IEditRoleDialogState {
  selectedUsers: ISelectionModel;
  selectedPlaybooks: ISelectionModel;
  selectedCapabilities: ISelectionModel;
}

class EditRoleDialogImpl extends Component<IEditRoleDialogProps, IEditRoleDialogState> {
  public static ROLE_DIALOG_FORM = "EDIT_ROLE_ADMIN";
  public static ROLE_DIALOG_FIELD_NAME = "roleName";
  public static ROLE_DIALOG_FIELD_DESCRIPTION = "roleDescription";

  constructor(props: IEditRoleDialogProps) {
    super(props);

    this.state = {
      selectedUsers: null,
      selectedPlaybooks: null,
      selectedCapabilities: null,
    };
  }

  handleSubmitData = (values): Promise<Role> => {
    const payload: InputRoleInput = {
      id: this.props.role.id,
      name: values[EditRoleDialogImpl.ROLE_DIALOG_FIELD_NAME],
      description: values[EditRoleDialogImpl.ROLE_DIALOG_FIELD_DESCRIPTION],
      userIds: this.state.selectedUsers.selectedIds,
      capabilityIds: this.state.selectedCapabilities.selectedIds.concat(this.state.selectedPlaybooks.selectedIds),
    };

    const storageFunction: any = this.props.role.id ? this.props.updateRole : this.props.createRole;

    return storageFunction(payload).then(
      (fulfilled: Role) => {
        this.props.onSelect(fulfilled);
        this.props.reset();
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error, with a general error that will be
      // available from this.props.error
      (rejected: Error) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  duplicateNameValidation: Validator = (value: any, _allValues?: any, _props?: any) => {
    // the name must not be already taken
    if (
      this.props.allRoles.find((r: Role) => r.name.toLowerCase() === value.toLowerCase() && r.id !== this.props.role.id)
    ) {
      return "There is another role with this name.";
    }
    // ok
    return undefined;
  };

  updateUsers = (selectionModel: ISelectionModel) => this.setState({ selectedUsers: selectionModel });
  updatePlaybooks = (selectionModel: ISelectionModel) => this.setState({ selectedPlaybooks: selectionModel });
  updateCapabilities = (selectionModel: ISelectionModel) => this.setState({ selectedCapabilities: selectionModel });

  render() {
    return (
      <TabDialog
        mainActionButtonProps={{
          children: "Save",
          isLoading: this.props.submitting,
          isDisabled: this.props.submitting,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        fullScreen={true}
        //width={1000}
        headerProps={{ title: `${this.props.titleMessage} ${this.props.roleName ? `'${this.props.roleName}'` : ""}` }}
        closeFn={this.props.onCancel}
        keepAlive
        empty
        footerMessage={{ message: this.props.error }}
        defaultSelectedKey={this.props.openTabKey}
      >
        <TabItem key="metadata" title={"Role Details"} icon={"settings_account_box"}>
          <Card
            contentClassName={style.card_center_content}
            className={clsx(style.card_fullscreen, style.card_fullscreen_with_tab)}
          >
            <SelForm onKeyDownCapture={handleTabInForm}>
              <InputReduxFormField
                componentClass="input"
                name={EditRoleDialogImpl.ROLE_DIALOG_FIELD_NAME}
                label="Name"
                placeholder="Enter role name"
                maxLength={MAX_LENGTH.ROLE.NAME}
                component={InputField}
                validate={[simpleFieldValidation, simpleCharactersValidation, this.duplicateNameValidation]}
              />

              <InputReduxFormField
                componentClass="input"
                name={EditRoleDialogImpl.ROLE_DIALOG_FIELD_DESCRIPTION}
                label="Description"
                placeholder="Enter role description"
                maxLength={MAX_LENGTH.ROLE.DESCRIPTION}
                component={InputField}
              />
            </SelForm>
          </Card>
        </TabItem>
        <TabItem key="role-users" title={"Users"} icon={"group"}>
          <RoleUsersAdminCard role={this.props.role} selectionModelUpdated={this.updateUsers} />
        </TabItem>
        <TabItem key="role-playbooks" title={"Playbooks"} icon={"desktop_windows"}>
          <RolePlaybooksAdminCard role={this.props.role} selectionModelUpdated={this.updatePlaybooks} />
        </TabItem>
        <TabItem key="role-capabilities" title={"Capabilities"} icon={"assignment_ind"}>
          <RoleCapabilitiesAdminCard role={this.props.role} selectionModelUpdated={this.updateCapabilities} />
        </TabItem>
      </TabDialog>
    );
  }
}

const EditRoleDialog = connect<IEditRoleDialogAppStateProps, IEditRoleDialogDispatchProps, IEditRoleDialogOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: EditRoleDialogImpl.ROLE_DIALOG_FORM,
  })(EditRoleDialogImpl),
);

export { EditRoleDialog };
export default EditRoleDialog;
